<template>
    <div class="custom-textarea-wrapper" :class="[classContainerAdd, { isNotrequired: !required }]">
        <!-- Label -->
        <BaseLabel
            :label="label"
            :required="required"
            :customLabelStyle="customLabelStyle"
            :custom-class="customLabelClass"
            :customSizeLabel="customSizeLabel"
        />

        <!-- Tooltip Message -->
        <InfoMessageBox v-if="tooltipMsg" class="ppv2-mt-2" :message="tooltipMsg" color="info" />

        <!-- CKEditor Mode -->
        <div
            class="textarea-container ppv2-overflow-hidden"
            :class="[
                state,
                { 'has-icon': iconIA, focused: isFocused, 'is-disabled ppv2-bg-black ppv2-bg-opacity-[10%]': disabled },
            ]"
        >
            <div
                v-if="useEditor"
                ref="editor"
                @input="emitValue"
                class="textarea-container ppv2-border ppv2-border-gray-300 ppv2-rounded-md ppv2-p-2 ppv2-bg-black ppv2-bg-opacity-[3%]"
                :style="editorStyle"
                :class="[{ 'ppv2-bg-gray-100 ppv2-cursor-not-allowed': disabled, 'ppv2-border-red-500': error }]"
            />
            <!-- Textarea Mode -->
            <textarea
                v-else
                :rows="rows"
                v-model="internalValue"
                :placeholder="placeholder"
                :disabled="disabled || state === 'disabled' || loading"
                v-bind="$attrs"
                class="custom-textarea ppv2-text-black ppv2-bg-black ppv2-bg-opacity-[3%]"
                :style="{ fontSize: normalizeFontSize(customSizeLabel, true) }"
                @input="emitValue"
                @focus="$emit('focus')"
                @blur="$emit('blur')"
            ></textarea>
            <!-- Loader -->
            <div v-if="loading" class="loading-overlay">
                <span class="spin"></span>
                <span class="loading-text ppv2-text-black">{{ $t('experience.step2.improveInProgress') }}</span>
            </div>
            <!-- ✨ Icône IA -->
            <div v-if="showImproveWithAI">
                <div v-if="iconIA && !loading" class="icon ia" @click="improveWithAI">
                    <span class="ia-generator ppv2-text-black" :style="{ fontSize: normalizeFontSize(13) }">{{
                        $t('experience.step2.improveWithAI')
                    }}</span>
                    <span v-if="!isDark" class="isFocused" />
                    <img v-if="isDark" src="/media/svg/aiDark.svg" alt="AI Dark" />
                </div>
            </div>
        </div>

        <!-- Description -->
        <p v-if="description" class="ppv2-text-xs ppv2-text-gray-500">{{ description }}</p>

        <!-- Error -->
        <p v-if="error" class="error-text">{{ error }}</p>
    </div>
</template>

<script>
import BaseLabel from './BaseLabel.vue'
import InfoMessageBox from './InfoMessageBox.vue'
import Editor from '@tinymce/tinymce-vue'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'BaseTextarea',
    components: {
        Editor,
        BaseLabel,
        InfoMessageBox,
    },
    props: {
        label: String,
        customLabelStyle: String,
        customLabelClass: String,
        classContainerAdd: String,
        value: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        rows: {
            type: Number,
            default: 4,
        },
        state: {
            type: String,
            default: 'default',
        },
        description: String,
        disabled: Boolean,
        error: String,
        required: Boolean,
        iconIA: Boolean,
        useEditor: {
            type: Boolean,
            default: true,
        },
        customSizeLabel: {
            type: Number,
            default: 15,
        },
        tooltipMsg: {
            type: String,
            default: '',
        },
        showImproveWithAI: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            internalValue: this.value,
            loading: false,
            isFocused: false,
            descriptionEditorInstance: null,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: [
                    'bold',
                    'italic',
                    'underline',
                    'link',
                    'bulletedList',
                    'numberedList',
                    'outdent',
                    'indent',
                    'undo',
                    'redo',
                ],
                content_style: `
                    body {
                        font-family: 'Poppins Light', sans-serif;
                        font-size: ${this.customSizeLabel}px;
                        line-height: 1.6;
                    }

                    p { margin: 0 0 10px; }
                    h1, h2, h3, h4, h5, h6 { margin: 10px 0; }
                `,
                placeholder: this.placeholder || this.$t('experience.step2.defaultText'),
            },
        }
    },
    watch: {
        value(val) {
            this.internalValue = val
            if (this.useEditor && this.descriptionEditorInstance && this.descriptionEditorInstance.getData() !== val) {
                this.descriptionEditorInstance.setData(this.formatForEditor(val))
            }
        },
        rows() {
            this.updateRowsHeight()
        },
        disabled(val) {
            if (this.descriptionEditorInstance) {
                this.descriptionEditorInstance.isReadOnly = val
            }
        },
    },
    mounted() {
        if (this.useEditor && this.$refs.editor) {
            this.editor
                .create(this.$refs.editor, this.editorConfig)
                .then((instance) => {
                    this.descriptionEditorInstance = instance
                    instance.setData(this.formatForEditor(this.internalValue) || '')

                    // Appliquer le thème initial ici
                    const editable = instance.ui.view.editable.element
                    if (editable) {
                        editable.classList.toggle('ck-dark', this.isDark)
                    }

                    // Écouter focus/blur
                    instance.editing.view.document.on('focus', () => {
                        this.isFocused = true
                        this.$emit('focus')
                        console.log('focus*****************************', this.isFocused)
                    })
                    instance.editing.view.document.on('blur', () => {
                        this.isFocused = false
                        this.$emit('blur')
                    })

                    // Écouter les changements de data
                    instance.model.document.on('change:data', () => {
                        this.internalValue = instance.getData()
                        this.emitValue()
                    })

                    if (this.disabled) {
                        instance.isReadOnly = true
                    }

                    this.updateRowsHeight()
                })
                .catch((err) => {
                    console.error('CKEditor init error', err)
                })
        }
    },
    beforeDestroy() {
        if (this.descriptionEditorInstance) {
            this.descriptionEditorInstance.destroy()
        }
    },

    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        editorStyle() {
            return {
                minHeight: `${this.rows * 25}px`,
            }
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        formatForEditor(val) {
            if (!val) return ''
            const hasHtmlTags = /<\s*(p|br|div|ul|ol|li|h[1-6])\b/i.test(val)
            if (hasHtmlTags) {
                return val
            }
            return val
                .split(/\n\s*\n/)
                .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
                .join('')
        },
        emitValue() {
            this.$emit('input', this.internalValue)
        },
        updateEditorContent() {
            if (this.useEditor && this.descriptionEditorInstance) {
                this.descriptionEditorInstance.setData(this.formatForEditor(this.internalValue))
            }
        },
        hasContent() {
            // Vérifie s'il y a du texte non vide dans l'éditeur ou le textarea
            return this.internalValue && this.internalValue.trim().length > 0
        },
        updateRowsHeight() {
            if (this.useEditor && this.descriptionEditorInstance) {
                const editable = this.descriptionEditorInstance.ui.view.editable.element
                if (editable) {
                    editable.style.minHeight = `${this.rows * 25}px`
                    // Find the main container to ensure it also expands
                    const mainContainer = editable.closest('.ck-editor__main')
                    if (mainContainer) {
                        mainContainer.style.height = 'auto'
                        mainContainer.style.minHeight = `${this.rows * 25}px`
                    }
                }
            }
        },

        async improveWithAI() {
            console.log('🚀 Amélioration IA déclenchée')
            if (this.disabled) return
            if (!this.hasContent()) {
                this.$toast.error(this.$t('common.enter_text_first'))
                return
            }

            this.loading = true
            this.disabled = true

            try {
                const currentContent = this.internalValue
                console.log("📤 Contenu envoyé à l'IA:", currentContent)

                const response = await fetch('/chat-research', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({
                        text: currentContent,
                        debug: true,
                    }),
                })

                if (!response.ok) {
                    throw new Error(`Erreur HTTP! statut: ${response.status}`)
                }

                const result = await response.json()
                console.log('📥 Résultat IA reçu:', result)

                if (result?.data?.data) {
                    const improvedContent = result.data.data
                    console.log('✨ Contenu amélioré:', improvedContent)

                    this.internalValue = improvedContent

                    this.$nextTick(() => {
                        this.updateEditorContent()
                        this.emitValue()
                    })

                    this.$toast.success(this.$t('common.text_enhanced_success'))
                } else {
                    throw new Error('Format de réponse invalide')
                }
            } catch (err) {
                console.error('❌ Erreur IA:', err)
                this.$toast.error("Erreur lors de l'amélioration: " + err.message)
            } finally {
                this.loading = false
                this.disabled = false
            }
        },
    },
}
</script>
<style scoped>
.isFocused {
    background-image: url('/media/svg/aiDark.svg');
    width: 15px;
    height: 12px;
    background-size: contain;
}
.custom-textarea-wrapper:focus-within .isFocused,
.custom-textarea-wrapper:hover .isFocused {
    background-image: url('/media/bo/icons/ai/ai.svg');
}
.custom-textarea-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    margin-bottom: 20px;
    opacity: 0.7;
}

.custom-textarea-wrapper:hover,
.custom-textarea-wrapper:focus-within {
    opacity: 1;
}

/* Container */
.textarea-container {
    display: flex;
    align-items: flex-start;
    position: relative;
    border-radius: 12px;
    border: 1px solid #9c9af880;
    transition: all 0.2s ease;
}

/* Textarea fallback */
.custom-textarea {
    font-family: 'Poppins Light', sans-serif;
    flex: 1;
    /*background: transparent;*/
    border: none;
    outline: none;
    resize: vertical;
    font-size: 0.95rem;
    line-height: 1.5;
    min-height: 80px;
    width: 100%;
    padding: 0.75rem 1rem;
}

/* États */
.textarea-container.disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.textarea-container.active {
    border-color: #9c9af8b2;
}

/*.textarea-container.valid {
    border-color: #31d0aa;
}*/

.textarea-container.error {
    border-color: #ed4b9e !important;
    background-color: initial !important;
}

/* Icons */
.icon {
    margin-left: 8px;
    font-size: 1rem;
    background-color: transparent;
}

/*.icon.valid {
    color: #31d0aa;
}*/

.icon.error {
    display: inline-block;
    color: #ed4b9e;
    background-color: initial !important;
    border: 1px solid;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    text-align: center;
    line-height: 20px;
    font-size: 15px;
}

.icon.ia {
    position: absolute;
    bottom: 12px;
    right: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
}

.ia-generator {
    opacity: 0.7;
    cursor: pointer;
}

.ia-generator.loading {
    pointer-events: none;
    opacity: 0.5;
}

.icon.ia img {
    width: 18px;
    height: 18px;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.icon.ia img:hover {
    opacity: 1;
}

.dark .icon.ia img {
    filter: brightness(0.8);
}

/* Error text */
.error-text {
    color: #ed4b9e;
    font-size: clamp(10px, 0.625vw, 22px);
}

.loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 12px;
    z-index: 20;
    gap: 10px;
}

.dark .loading-overlay {
    background: #f4fafe0d;
}
.spin {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid #ccc;
    border-top-color: #9c9af8;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-text {
    font-size: 0.85rem;
}

:deep(
        .ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable p,
        .ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners p
    ) {
    margin: 0;
}
:deep(
        .ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
        .ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners
    ) {
    background: transparent !important;
    border: none !important;
    font-size: clamp(10px, 0.78125vw, 25px);
    font-family: 'Poppins Light', sans-serif;
    padding: 10px 17px;
}

:deep(.ck-editor__editable_inline) {
    width: 100% !important;
    min-height: 80px; /* Base minimum */
    box-sizing: border-box;
}

:deep(.ck.ck-editor__main) {
    width: 100% !important;
    height: auto;
    overflow: auto;
}

:deep(.ck.ck-editor) {
    width: 100% !important;
}
/* Cacher complètement le textarea quand il n'est pas utilisé */
.custom-textarea[v-show='false'] {
    display: none !important;
}
.custom-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    opacity: 0.7;
}

.custom-input-wrapper:hover,
.custom-input-wrapper:focus-within {
    opacity: 1;
}

/* Add this to your <style scoped> */

.textarea-container.is-disabled {
    border-color: #d1d1d1 !important;
    cursor: not-allowed;
    opacity: 0.8;
}

.textarea-container.is-disabled .custom-textarea {
    cursor: not-allowed;
    color: #888888 !important;
}

/* Ensure CKEditor also looks disabled */
:deep(.ck-editor__editable.ck-read-only) {
    background-color: #f5f5f5 !important;
    cursor: not-allowed !important;
}

/* Prevent the wrapper from lighting up on hover if disabled */
.custom-textarea-wrapper.is-disabled {
    opacity: 0.5 !important;
    pointer-events: none;
}
</style>
